import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import LeafNetLogo from "./LeafNetLogo";
import {
  UI_CLASSIC,
  UI_NEW,
  getUiMode,
  setUiMode,
  subscribeUiMode,
  hasSeenUiIntro,
  markUiIntroSeen,
} from "./uiMode";

const TOAST_MS = 5000;

/**
 * The Classic / New UI switch in the sidebar (NavBarUser.jsx). It looks like the classic
 * sidebar until the new UI is on, then newUi.css restyles it. Classic users who have not
 * answered yet get a one-time "new look" note under it; switching to the new UI shows a
 * short toast with a way back.
 */
export default function UiModeSwitch() {
  const [mode, setMode] = useState(getUiMode);
  const [introOpen, setIntroOpen] = useState(
    () => getUiMode() === UI_CLASSIC && !hasSeenUiIntro()
  );
  const [toastOpen, setToastOpen] = useState(false);
  const toastTimer = useRef(null);

  useEffect(() => subscribeUiMode(setMode), []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const choose = (next) => {
    markUiIntroSeen();
    setIntroOpen(false);
    clearTimeout(toastTimer.current);
    if (next === mode) return;
    setUiMode(next);
    if (next === UI_NEW) {
      setToastOpen(true);
      toastTimer.current = setTimeout(() => setToastOpen(false), TOAST_MS);
    } else {
      setToastOpen(false);
    }
  };

  const dismissIntro = () => {
    markUiIntroSeen();
    setIntroOpen(false);
  };

  const isNew = mode === UI_NEW;

  return (
    <div className="lnui-switch-wrap">
      <div className="lnui-switch-label" id="lnui-switch-label">
        Interface
      </div>
      <div className="lnui-switch" role="group" aria-labelledby="lnui-switch-label">
        <button
          type="button"
          className={`lnui-switch-btn${isNew ? "" : " is-on"}`}
          aria-pressed={!isNew}
          onClick={() => choose(UI_CLASSIC)}
        >
          Classic
        </button>
        <button
          type="button"
          className={`lnui-switch-btn${isNew ? " is-on" : ""}`}
          aria-pressed={isNew}
          onClick={() => choose(UI_NEW)}
        >
          {isNew ? <LeafNetLogo size={14} /> : null}
          New UI
          {isNew ? null : <span className="lnui-new-tag">New</span>}
        </button>
      </div>

      {introOpen ? (
        <div className="lnui-callout" role="note">
          <div className="lnui-callout-title">
            <LeafNetLogo size={24} />
            LeafNet has a new look
          </div>
          <div className="lnui-callout-text">
            Your data and menus stay the same, and you can switch back from here at any
            time.
          </div>
          <div className="lnui-callout-actions">
            <button
              type="button"
              className="lnui-btn lnui-btn--primary"
              onClick={() => choose(UI_NEW)}
            >
              Try it
            </button>
            <button type="button" className="lnui-btn" onClick={dismissIntro}>
              Not now
            </button>
          </div>
        </div>
      ) : null}

      {toastOpen
        ? ReactDOM.createPortal(
            <div className="lnui-toast" role="status">
              <span>
                You're on the <b>new UI</b>. We'll remember this choice.
              </span>
              <button type="button" onClick={() => choose(UI_CLASSIC)}>
                Switch back
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
